import React from "react";
import * as Styled from "./styles";
import { useClinicalBudget } from "shared/providers/ClinicalBudget";
import { useAuth } from "shared/providers/auth";
import { getAreas } from "requests/ClinicalBudget";
import { IProcedimento, IArea } from "shared/types/ClinicalBudget";
import { IoSearch, IoFolderOpen, IoChevronDown, IoPricetag, IoExpandOutline, IoContractOutline, IoLockClosed, IoClose } from "react-icons/io5";
import toast from "react-hot-toast";

const normalize = (str: string) =>
  str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const BudgetProcedures: React.FC = () => {
  const { user } = useAuth();
  const {
    areas,
    setAreas,
    loadingAreas,
    setLoadingAreas,
    setSelectedProc,
    setSelectedArea,
    setShowCategoriasModal,
    setShowOdontograma,
    setShowArcadaModal,
    setShowObsModal,
    setSelectedCategoria,
    setSelectedGrupo,
    setSelectedValor,
    setSelectedCatprocId,
    formatMoeda,
    showValues,
    setFacesRef,
    setElementosRef,
    isAreaOrtodontia,
    temItemOrtodontia,
    temItemOutraArea,
  } = useClinicalBudget();

  const [searchTerm, setSearchTerm] = React.useState<string>('');
  const [expandedAreas, setExpandedAreas] = React.useState<Set<number>>(new Set());
  const allAreasRef = React.useRef<IArea[]>([]);

  React.useEffect(() => {
    const load = async () => {
      setLoadingAreas(true);
      try {
        const userId = user?.data?.id || "";
        const response = await getAreas(userId);

        if (response.data?.success && response.data.areas) {
          allAreasRef.current = response.data.areas;
          setAreas(response.data.areas);
          if (response.data.faces) setFacesRef(response.data.faces);
          if (response.data.elementos) setElementosRef(response.data.elementos);
          if (response.data.areas.length > 0) {
            setExpandedAreas(new Set([response.data.areas[0].id]));
          }
        }
      } catch (error) {
        console.error("Erro ao carregar áreas:", error);
      } finally {
        setLoadingAreas(false);
      }
    };
    load();
  }, [user, setAreas, setLoadingAreas, setFacesRef, setElementosRef]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    const trimmed = value.trim();

    if (trimmed.length === 0) {
      setAreas(allAreasRef.current);
      if (allAreasRef.current.length > 0) {
        setExpandedAreas(new Set([allAreasRef.current[0].id]));
      }
      return;
    }

    const term = normalize(trimmed);
    const filtered: IArea[] = [];

    for (const area of allAreasRef.current) {
      const procs = area.procedimentos.filter(
        (p) => normalize(p.nome).includes(term) || String(p.id).includes(trimmed)
      );
      if (procs.length > 0) {
        filtered.push({ ...area, procedimentos: procs });
      }
    }

    setAreas(filtered);
    setExpandedAreas(new Set(filtered.map((a) => a.id)));
  };

  const clearSearch = () => {
    setSearchTerm('');
    setAreas(allAreasRef.current);
    if (allAreasRef.current.length > 0) {
      setExpandedAreas(new Set([allAreasRef.current[0].id]));
    }
  };

  const toggleArea = (areaId: number) => {
    setExpandedAreas(prev => {
      const newSet = new Set(prev);
      if (newSet.has(areaId)) {
        newSet.delete(areaId);
      } else {
        newSet.add(areaId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedAreas(new Set(areas.map(a => a.id)));
  };

  const collapseAll = () => {
    setExpandedAreas(new Set());
  };

  const isProcBloqueado = React.useCallback((areaNome: string): boolean => {
    const procIsOrto = isAreaOrtodontia(areaNome);
    return (temItemOrtodontia && !procIsOrto) || (temItemOutraArea && procIsOrto);
  }, [isAreaOrtodontia, temItemOrtodontia, temItemOutraArea]);

  const handleAddProc = (proc: IProcedimento, areaNome: string) => {
    const procIsOrto = isAreaOrtodontia(areaNome);
    if ((temItemOrtodontia && !procIsOrto) || (temItemOutraArea && procIsOrto)) {
      toast.error(
        procIsOrto
          ? 'O orçamento já possui procedimentos de outras áreas. Não é possível adicionar Ortodontia ao mesmo orçamento.'
          : 'O orçamento já possui procedimentos de Ortodontia. Não é possível adicionar procedimentos de outras áreas ao mesmo orçamento.',
        { duration: 4000 }
      );
      return;
    }

    setSelectedProc(proc);
    setSelectedArea(areaNome);
    setSelectedGrupo(null);
    setSelectedCategoria(null);
    setSelectedValor(proc.valor);
    setSelectedCatprocId(0);

    if (proc.grupos > 0) {
      setShowCategoriasModal(true);
    } else {
      if (proc.usa_arcada || proc.usa_hemiarcada) {
        setShowArcadaModal(true);
      } else if (proc.usa_elemento) {
        setShowOdontograma(true);
      } else {
        setShowObsModal(true);
      }
    }
  };

  if (loadingAreas) {
    return (
      <Styled.LoadingContainer>
        <div className="spinner" />
        <p>Carregando procedimentos...</p>
      </Styled.LoadingContainer>
    );
  }

  return (
    <>
      <Styled.SearchContainer>
        <IoSearch className="icon" />
        <input
          type="text"
          placeholder="Buscar procedimento por nome ou código..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
        />
        {searchTerm && (
          <button className="clear-btn" onClick={clearSearch} type="button">
            <IoClose size={18} />
          </button>
        )}
      </Styled.SearchContainer>

      {areas.length > 0 && (
        <Styled.ExpandButtons>
          <Styled.ExpandButton onClick={expandAll}>
            <IoExpandOutline size={16} />
            Expandir Todos
          </Styled.ExpandButton>
          <Styled.ExpandButton onClick={collapseAll}>
            <IoContractOutline size={16} />
            Recolher Todos
          </Styled.ExpandButton>
        </Styled.ExpandButtons>
      )}

      {areas.length === 0 ? (
        <Styled.EmptyState>
          <span className="icon">⚠️</span>
          <p>Nenhum procedimento encontrado</p>
        </Styled.EmptyState>
      ) : (
        <Styled.Accordion>
          {areas.map(area => {
            const areaIsOrto = isAreaOrtodontia(area.nome);
            return (
            <Styled.AreaCard key={area.id}>
              <Styled.AreaHeader
                expanded={expandedAreas.has(area.id)}
                $isOrto={areaIsOrto}
                onClick={() => toggleArea(area.id)}
              >
                <Styled.AreaTitle $isOrto={areaIsOrto}>
                  <IoFolderOpen className="icon" />
                  <span>
                    {area.nome}
                    {areaIsOrto && <Styled.OrtoTag>Ortodontia</Styled.OrtoTag>}
                  </span>
                </Styled.AreaTitle>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Styled.AreaBadge $isOrto={areaIsOrto}>{area.procedimentos.length} proc.</Styled.AreaBadge>
                  <Styled.ChevronIcon expanded={expandedAreas.has(area.id)}>
                    <IoChevronDown />
                  </Styled.ChevronIcon>
                </div>
              </Styled.AreaHeader>

              <Styled.AreaContent expanded={expandedAreas.has(area.id)}>
                {area.procedimentos.map(proc => (
                  <Styled.ProcItem key={proc.id}>
                    <Styled.ProcInfo>
                      <Styled.ProcNome>{proc.nome}</Styled.ProcNome>
                      <Styled.ProcMeta>
                        <Styled.ProcValor showValue={showValues}>
                          {showValues ? `R$ ${formatMoeda(proc.valor)}` : '***'}
                        </Styled.ProcValor>
                        {proc.grupos > 0 && (
                          <Styled.ProcGrupos>
                            <IoPricetag size={14} />
                            {proc.grupos} categoria(s)
                          </Styled.ProcGrupos>
                        )}
                      </Styled.ProcMeta>
                    </Styled.ProcInfo>
                    <Styled.AddButton
                      $blocked={isProcBloqueado(area.nome)}
                      onClick={() => handleAddProc(proc, area.nome)}
                    >
                      {isProcBloqueado(area.nome) && <IoLockClosed size={12} />}
                      {isProcBloqueado(area.nome) ? 'Bloqueado' : 'Adicionar'}
                    </Styled.AddButton>
                  </Styled.ProcItem>
                ))}
              </Styled.AreaContent>
            </Styled.AreaCard>
            );
          })}
        </Styled.Accordion>
      )}

      {areas.length > 0 && (
        <Styled.Stats>
          <span>{areas.length} área(s)</span>
          <span>•</span>
          <span>{areas.reduce((acc, a) => acc + a.procedimentos.length, 0)} procedimento(s)</span>
        </Styled.Stats>
      )}
    </>
  );
};

export { BudgetProcedures };
